import { createContext, useCallback, useContext } from 'react';
import { matchPath } from 'react-router-dom';

// The Portal pages that a parent uses to buy and manage, remounted for an
// independent student under `/learn/account/*` (student-self-signup-prd.md
// §17, D-SSU-23). Same components, same API calls: on `/learn/*` the API client
// sends the kid token, and the backend family-manager rule admits the student
// for their own Family.
//
// A Portal page links with Portal paths. `useAccountPath()` turns those into
// the matching Learn account path when the page renders inside Learn, so a
// student never lands on a `/portal/*` page that would ask them to sign in as
// a parent.

export type AccountSurface = 'portal' | 'learn';

export const LEARN_ACCOUNT_BASE = '/learn/account';

/**
 * Portal routes that have a Learn account twin. The suffix after `/portal` is
 * the same under `/learn/account`. Everything else is parent-only (adding kids,
 * PINs, approvals, Hangar pairing, family invites) and maps to the account home.
 */
export const STUDENT_ACCOUNT_ROUTES = [
  'membership',
  'wallet',
  'wallet/topup',
  'wallet/auto-topup',
  'wallet/stars-guide',
  'billing',
  'usage',
  'usage/:kidId',
  'settings',
  'courses',
  'courses/:courseSlug',
  'checkout/class/:classId',
  'tutoring',
  'tutoring/:slug',
  'self-paced',
  'self-paced/:slug',
  'academy',
  'academy/hsc-planner',
  'academy/products/:slug',
  'academy/checkout/:sku',
  'academy/orders/:intentId',
  'academy/reports/:sessionId',
  'challenge/:slug',
  'challenge/:slug/register',
  'challenge/:slug/report',
  'study',
  'family/:kidId/study',
  'pathway',
  'guides',
] as const;

/** Which surface a Portal page renders in. `AccountSurfaceProvider` sets it. */
export const AccountSurfaceContext = createContext<AccountSurface>('portal');

export function useAccountSurface(): AccountSurface {
  return useContext(AccountSurfaceContext);
}

/**
 * Map a Portal path (optionally with `?query` / `#hash`) to its Learn account
 * twin. Non-Portal paths pass through. A Portal path with no twin goes to the
 * account home.
 */
export function toLearnAccountPath(portalPath: string): string {
  const cut = portalPath.search(/[?#]/);
  const pathname = cut === -1 ? portalPath : portalPath.slice(0, cut);
  const rest = cut === -1 ? '' : portalPath.slice(cut);
  if (pathname === '/portal' || pathname === '/portal/') return LEARN_ACCOUNT_BASE;
  if (!pathname.startsWith('/portal/')) return portalPath;
  const suffix = pathname.slice('/portal/'.length).replace(/\/$/, '');
  const supported = STUDENT_ACCOUNT_ROUTES.some((route) =>
    matchPath({ path: `/${route}`, end: true }, `/${suffix}`),
  );
  return supported ? `${LEARN_ACCOUNT_BASE}/${suffix}${rest}` : LEARN_ACCOUNT_BASE;
}

/** Returns a mapper for Portal paths that respects the current surface. */
export function useAccountPath(): (portalPath: string) => string {
  const surface = useAccountSurface();
  return useCallback(
    (portalPath: string) => (surface === 'learn' ? toLearnAccountPath(portalPath) : portalPath),
    [surface],
  );
}
